<template>
  <tr>
    <!-- Title with Autocomplete -->
    <td style="position: relative">
      <input
        type="text"
        class="input"
        v-model="searchTitle"
        @input="onTyping"
        @blur="hideSuggestions"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown="checkSpecialKey"
        placeholder="Title"
        autocomplete="off"
      />
      <!-- Autocomplete -->
      <ul
        v-show="showSuggestions && filteredSuggestions.length"
        class="box"
        style="
          position: absolute;
          width: 100%;
          z-index: 10;
          max-height: 200px;
          overflow-y: auto;
          padding: 0;
        "
      >
        <li
          v-for="(suggestion, index) in filteredSuggestions"
          :key="suggestion.id"
          @mousedown.prevent="selectSuggestion(suggestion)"
          :class="{ 'is-active': highlightedIndex === index }"
          style="cursor: pointer; padding: 5px; list-style: none"
        >
          {{ suggestion.name }}
        </li>
      </ul>
    </td>
    <td>
      <input
        class="input"
        type="text"
        v-model="item.description"
        placeholder="Description"
      />
    </td>

    <!-- Quantity -->
    <td>
      <input
        class="input"
        type="number"
        min="0"
        step="0.001"
        v-model.number="item.quantity"
        @input="onQuantityInput"
      />
    </td>

    <!-- Unit Price -->
    <td>
      <input
        class="input"
        type="number"
        min="0"
        step="0.001"
        v-model="item.unit_price"
        @input="updateAmounts"
      />
    </td>

    <!-- VAT Code -->
    <td>
      <div class="select is-fullwidth">
        <select v-model="item.vat_code" @change="onVatChange">
          <option :value="null" disabled>Select VAT code...</option>
          <option v-for="code in vatCodes" :key="code.id" :value="code.id">
            {{ formatVatLabel(code) }}
          </option>
        </select>
      </div>
    </td>

    <!-- Net -->
    <td>
      <input class="input is-static" :value="netAmount" readonly />
    </td>

    <!-- VAT -->
    <td>
      <input class="input is-static" :value="vatAmount" readonly />
    </td>

    <!-- Gross -->
    <td>
      <input class="input is-static" :value="grossAmount" readonly />
    </td>

    <!-- Actions -->
    <td class="has-text-centered">
      <button class="delete is-small" @click="$emit('remove')"></button>
      <button
        class="button is-small is-light ml-1"
        @click="openDescriptionModal"
        title="Edit description"
      >
        <span class="icon is-small">
          <i class="fas fa-edit"></i>
        </span>
      </button>
    </td>
  </tr>

  <!-- Description Modal -->
  <Teleport to="body">
    <div v-if="showModal" class="modal is-active">
      <div class="modal-background" @click="closeDescriptionModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Edit Description</p>
          <button class="delete" @click="closeDescriptionModal"></button>
        </header>
        <section class="modal-card-body">
          <textarea
            class="textarea"
            v-model="tempDescription"
            rows="5"
          ></textarea>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="saveDescription">
            Save
          </button>
          <button class="button" @click="closeDescriptionModal">Cancel</button>
        </footer>
      </div>
    </div>
  </Teleport>

  <!-- Search Modal (triggered by *) -->
  <Teleport to="body">
    <div v-if="showSearchModal" class="modal is-active">
      <div class="modal-background" @click="closeSearchModal"></div>
      <div class="modal-card" style="width: 700px; max-width: 95vw">
        <header class="modal-card-head">
          <p class="modal-card-title">Advanced Item Search</p>
          <button class="delete" @click="closeSearchModal"></button>
        </header>
        <section class="modal-card-body">
          <p class="mb-3">🔎 Here you can implement a bigger search UI.</p>
          <input
            class="input"
            type="text"
            placeholder="Search items..."
            v-model="searchModalQuery"
          />
          <ul class="mt-3">
            <li
              v-for="i in modalFilteredItems"
              :key="i.id"
              @click="
                selectSuggestion(i);
                closeSearchModal();
              "
              style="cursor: pointer; padding: 5px"
            >
              {{ i.name }} — {{ i.unit_price }} €
            </li>
          </ul>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script>
import api from '@/utils/axios';
export default {
  name: 'ItemForm',
  props: {
    initialItem: Object,
    itemIndex: Number,
    itemsAvailable: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      item: this.initialItem,
      searchTitle: this.initialItem.title || '',
      showSuggestions: false,
      highlightedIndex: 0,
      showModal: false,
      tempDescription: this.initialItem.description || '',
      vatCodes: [],
      showSearchModal: false,
      searchModalQuery: '',
    };
  },
  computed: {
    netAmount() {
      const qty = Number(this.item.quantity) || 0;
      const price = Number(this.item.unit_price) || 0;
      return (qty * price).toFixed(2);
    },
    selectedVatPercent() {
      const code = this.vatCodes.find((c) => c.id === this.item.vat_code);
      return code ? Number(code.percent) : 0;
    },
    vatAmount() {
      const net = parseFloat(this.netAmount) || 0;
      return (net * (this.selectedVatPercent / 100)).toFixed(2);
    },
    grossAmount() {
      const net = parseFloat(this.netAmount) || 0;
      const vat = parseFloat(this.vatAmount) || 0;
      return (net + vat).toFixed(2);
    },
    filteredSuggestions() {
      const search = this.searchTitle.toLowerCase();
      return this.itemsAvailable.filter((item) =>
        (item.name || '').toLowerCase().includes(search)
      );
    },
    modalFilteredItems() {
      const q = this.searchModalQuery.toLowerCase();
      return this.itemsAvailable.filter((i) =>
        (i.name || '').toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    this.loadVatCodes();
  },
  methods: {
    async loadVatCodes() {
      try {
        const res = await api.get('/accounting/vat-codes/');
        this.vatCodes = res.data;
      } catch (error) {
        console.error('Error loading VAT codes:', error);
      }
    },
    updateAmounts() {
      this.$emit('updatePrice', this.item);
    },
    onTyping() {
      this.item.title = this.searchTitle;
      this.showSuggestions = !!this.searchTitle.trim();
      this.highlightedIndex = 0;
    },
    selectSuggestion(suggestion) {
      this.item.item_id = suggestion.id;
      this.item.title = suggestion.name;
      this.item.unit_price = suggestion.unit_price;
      this.item.description = suggestion.description;
      this.tempDescription = suggestion.description || '';
      this.searchTitle = suggestion.name;
      this.showSuggestions = false;
      this.updateAmounts();
    },
    selectHighlighted() {
      const suggestion = this.filteredSuggestions[this.highlightedIndex];
      if (suggestion) {
        this.selectSuggestion(suggestion);
      }
    },
    moveDown() {
      if (this.highlightedIndex < this.filteredSuggestions.length - 1) {
        this.highlightedIndex++;
      }
    },
    moveUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },
    hideSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 300);
    },
    checkSpecialKey(e) {
      if (e.key === '*') {
        this.openSearchModal();
      }
    },
    openSearchModal() {
      this.showSearchModal = true;
    },
    closeSearchModal() {
      this.showSearchModal = false;
      this.searchModalQuery = '';
    },
    openDescriptionModal() {
      this.tempDescription = this.item.description || '';
      this.showModal = true;
    },
    closeDescriptionModal() {
      this.showModal = false;
    },
    saveDescription() {
      this.item.description = this.tempDescription;
      this.updateAmounts();
      this.closeDescriptionModal();
    },
    onVatChange() {
      const code = this.vatCodes.find((c) => c.id === this.item.vat_code);
      if (code) {
        this.item.vat_code_detail = {
          id: code.id,
          code: code.code,
          percent: code.percent,
          description: code.description,
          peppol_category: code.peppol_category, // IMPORTANT
        };
      }
      this.updateAmounts();
    },
    formatVatLabel(code) {
      if (code.peppol_category === 'AE') {
        return `${code.code} - 0% (Reverse charge)`;
      }

      if (code.peppol_category === 'E') {
        return `${code.code} - 0% (Exempt)`;
      }

      return `${code.code} - ${code.percent}%`;
    },
    onQuantityInput() {
      // Prevent negative values
      if (this.item.quantity < 0) {
        this.item.quantity = 0;
      }

      // Recompute prices
      this.updateAmounts();
    },
  },
};
</script>

<style scoped>
.is-active {
  background-color: #00d1b2;
  color: white;
}
</style>
